import * as supabase from '@supabase/supabase-js';
import { SUPABASE_KEY, SUPABASE_URL } from '../config.js';
import { notify } from '../lib/notify.js';
import { decryptObj, encryptObj } from './crypto.js';

export const sb = supabase.createClient(SUPABASE_URL, SUPABASE_KEY);

// Fixed row ids for the single-row "settings" kinds (overwritten in place).
// Children are NOT here — they're stored one row per child (see below).
const ROW_IDS = {
  concentration: '00000000-0000-0000-0000-000000000002',
  otherMed:      '00000000-0000-0000-0000-000000000003',
};

// ----- offline cache (ciphertext only — keeps E2E intact at rest) -----
const CACHE_KEY = 'medRowsCache';
export const cacheRows = (rows) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(rows)); } catch (e) {} };
export const readCachedRows = () => { try { const s = localStorage.getItem(CACHE_KEY); return s ? JSON.parse(s) : null; } catch (e) { return null; } };

// ----- data layer (all values encrypted before they leave the device) -----
export async function fetchAllRows() {
  const { data, error } = await sb.from('entries').select('*');
  if (error) throw error;
  return data || [];
}
// Decrypt rows into the app's in-memory shape. Rows that fail to decrypt
// are skipped (and counted) rather than aborting the whole load, so one bad
// row can't hide everything.
export async function decodeRows(rows) {
  const out = {
    children: [],
    history: [],
    temperatures: [],
    concentrations: { paracetamol: [], ibuprofen: [] },
    otherMeds: [{ name: 'Antibiotics', category: 'Other' }],
    failed: 0,
    legacyChildRowId: null,
  };
  for (const r of rows) {
    let payload;
    try { payload = await decryptObj(r.ciphertext, r.iv); }
    catch (e) { out.failed++; continue; }
    if (r.kind === 'history') out.history.push({ ...payload, id: r.id });
    else if (r.kind === 'temperature') out.temperatures.push({ ...payload, id: r.id });
    else if (r.kind === 'child') {
      if (Array.isArray(payload)) { // legacy single-row format → migrate on next save
        out.legacyChildRowId = r.id;
        for (const c of payload) out.children.push(c);
      } else {
        out.children.push({ ...payload, id: r.id });
      }
    }
    else if (r.kind === 'concentration') out.concentrations = payload;
    // Normalize to {name, category}: legacy rows stored plain strings,
    // and be defensive about anything malformed (missing name, nulls)
    // so one odd entry can't crash the whole medication list.
    else if (r.kind === 'otherMed') {
      const normalized = (Array.isArray(payload) ? payload : [])
        .map(m => typeof m === 'string' ? { name: m, category: 'Other' }
          : (m && m.name ? { name: m.name, category: m.category || 'Other' } : null))
        .filter(Boolean);
      // Keep the default seed rather than leaving the app with no meds.
      if (normalized.length) out.otherMeds = normalized;
    }
  }
  out.history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  out.temperatures.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return out;
}
// True if the current key can read the data (or there's nothing to read yet).
export async function verifyKeyAgainst(rows) {
  if (!rows.length) return true;
  for (const r of rows) { try { await decryptObj(r.ciphertext, r.iv); return true; } catch (e) {} }
  return false;
}
export async function saveKindRow(kind, data) {
  const { ciphertext, iv } = await encryptObj(data);
  const { error } = await sb.from('entries').upsert(
    { id: ROW_IDS[kind], kind, ciphertext, iv, updated_at: new Date().toISOString() });
  if (error) throw error;
}
export async function insertHistoryRow(entry) {
  const { ciphertext, iv } = await encryptObj(entry);
  const { error } = await sb.from('entries').insert(
    { id: entry.id, kind: 'history', ciphertext, iv });
  if (error) throw error;
}
// Editing a logged dose overwrites its row in place (id stays the same).
export async function updateHistoryRow(entry) {
  const { id, ...rest } = entry;
  const { ciphertext, iv } = await encryptObj(rest);
  const { error } = await sb.from('entries').upsert(
    { id, kind: 'history', ciphertext, iv, updated_at: new Date().toISOString() });
  if (error) throw error;
}
// Temperature readings are an append-only log too (one encrypted row each).
export async function insertTemperatureRow(entry) {
  const { ciphertext, iv } = await encryptObj(entry);
  const { error } = await sb.from('entries').insert(
    { id: entry.id, kind: 'temperature', ciphertext, iv });
  if (error) throw error;
}
// A corrected reading overwrites its own row, mirroring updateHistoryRow.
export async function updateTemperatureRow(entry) {
  const { id, ...rest } = entry;
  const { ciphertext, iv } = await encryptObj(rest);
  const { error } = await sb.from('entries').upsert(
    { id, kind: 'temperature', ciphertext, iv, updated_at: new Date().toISOString() });
  if (error) throw error;
}
// One row per child (id = the child's UUID), so two phones editing
// different children never overwrite each other.
export async function upsertChildRow(child) {
  const { id, ...rest } = child;
  const { ciphertext, iv } = await encryptObj(rest);
  const { error } = await sb.from('entries').upsert(
    { id, kind: 'child', ciphertext, iv, updated_at: new Date().toISOString() });
  if (error) throw error;
}
export async function deleteRow(id) {
  const { error } = await sb.from('entries').delete().eq('id', id);
  if (error) throw error;
}
export const syncFail = (e) => { console.error('Sync error:', e); notify('Sync failed — check your connection. Your entry is shown locally but may not have saved to the cloud.', { duration: 8000 }); };
// ===== end Supabase block =====
